import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import DictationButton from '../components/DictationButton';

export const INTake = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [customWorldview, setCustomWorldview] = useState('');
  const [customRelationship, setCustomRelationship] = useState('');
  const [profile, setProfile] = useState({
    age: '',
    gender: '',
    relationship_status: '',
    has_children: null,
    country: '',
    work_type: '',
    worldview: '',
  });

  useEffect(() => {
    checkProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      if (response.data.worldview) {
        setHasProfile(true);
        navigate('/assessment');
      }
    } catch (error) {
      console.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // If worldview is "other", use the custom text
    const finalWorldview = profile.worldview === 'other' && customWorldview 
      ? `other: ${customWorldview}` 
      : profile.worldview;

    // If relationship is "other", use the custom text
    const finalRelationship = profile.relationship_status === 'other' && customRelationship
      ? `other: ${customRelationship}`
      : profile.relationship_status;

    try {
      await api.post('/user/profile', {
        age: parseInt(profile.age) || null,
        gender: profile.gender || null,
        relationship_status: finalRelationship || null,
        has_children: profile.has_children,
        country: profile.country || null,
        work_type: profile.work_type || null,
        worldview: finalWorldview || null,
      });
      toast.success(t('intake.profileSaved'));
      navigate('/assessment');
    } catch (error) {
      toast.error(t('intake.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <User className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
            {t('intake.title')}
          </h1>
          <p className="font-sans font-light text-[#D1D1D6] text-lg">
            {t('intake.subtitle')}
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="glass-panel rounded-2xl p-8 space-y-6"
        >
          {/* Age */}
          <div>
            <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
              {t('intake.age')}
            </label>
            <input
              type="number"
              value={profile.age}
              onChange={(e) => setProfile({ ...profile, age: e.target.value })}
              className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
              placeholder={t('intake.agePlace')}
              data-testid="age-input"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
              {t('intake.gender')}
            </label>
            <select
              value={profile.gender}
              onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
              className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
              data-testid="gender-select"
            >
              <option value="">{t('intake.selectGender')}</option>
              <option value="male">{t('intake.male')}</option>
              <option value="female">{t('intake.female')}</option>
              <option value="non-binary">{t('intake.nonBinary')}</option>
              <option value="other">{t('intake.other')}</option>
            </select>
          </div>

          {/* Relationship Status */}
          <div>
            <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
              {t('intake.relationshipStatus')}
            </label>
            <select
              value={profile.relationship_status}
              onChange={(e) => setProfile({ ...profile, relationship_status: e.target.value })}
              className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
              data-testid="relationship-select"
            >
              <option value="">{t('intake.selectRelationship')}</option>
              <option value="single">{t('intake.single')}</option>
              <option value="relationship">{t('intake.relationship')}</option>
              <option value="married">{t('intake.married')}</option>
              <option value="divorced">{t('intake.divorced')}</option>
              <option value="widowed">{t('intake.widowed')}</option>
              <option value="its_complicated">{t('intake.itsComplicated')}</option>
              <option value="other">{t('intake.relationshipOther')}</option>
            </select>

            {/* Custom relationship text input when "Other" is selected */}
            <AnimatePresence>
              {profile.relationship_status === 'other' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3">
                    <div className="relative">
                      <input
                        type="text"
                        value={customRelationship}
                        onChange={(e) => setCustomRelationship(e.target.value)}
                        className="w-full bg-white/5 border border-[#D4AF37]/30 rounded-lg px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
                        placeholder={language === 'tr' ? 'İlişki durumunuzu tanımlayın...' : 'Describe your relationship status...'}
                        data-testid="custom-relationship-input"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <DictationButton
                          onTranscription={(text) => setCustomRelationship(prev => prev + (prev ? ' ' : '') + text)}
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Children */}
          <div>
            <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-3">
              {t('intake.children')}
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setProfile({ ...profile, has_children: true })}
                className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                  profile.has_children === true
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#F8F5F2]'
                    : 'border-[#D4AF37]/20 text-[#A1A1AA] hover:border-[#D4AF37]/40'
                }`}
                data-testid="children-yes"
              >
                {t('intake.yes')}
              </button>
              <button
                type="button"
                onClick={() => setProfile({ ...profile, has_children: false })}
                className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                  profile.has_children === false
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#F8F5F2]'
                    : 'border-[#D4AF37]/20 text-[#A1A1AA] hover:border-[#D4AF37]/40'
                }`}
                data-testid="children-no"
              >
                {t('intake.no')}
              </button>
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
              {t('intake.country')}
            </label>
            <input
              type="text"
              value={profile.country}
              onChange={(e) => setProfile({ ...profile, country: e.target.value })}
              className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
              placeholder={t('intake.countryPlace')}
              data-testid="country-input"
            />
          </div>

          {/* Worldview - THE MOST IMPORTANT */}
          <div className="border-t border-[#D4AF37]/20 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold">
                {t('intake.worldview')}
              </label>
            </div>
            <p className="font-sans text-sm text-[#A1A1AA] mb-4">
              {t('intake.worldviewDesc')}
            </p>
            <div className="space-y-3">
              {[
                { value: 'religious', label: t('intake.religious'), desc: t('intake.religiousDesc') },
                { value: 'spiritual', label: t('intake.spiritual'), desc: t('intake.spiritualDesc') },
                { value: 'scientific', label: t('intake.scientific'), desc: t('intake.scientificDesc') },
                { value: 'philosophical', label: t('intake.philosophical'), desc: t('intake.philosophicalDesc') },
                { value: 'humanistic', label: t('intake.humanistic'), desc: t('intake.humanisticDesc') },
                { value: 'exploring', label: t('intake.exploring'), desc: t('intake.exploringDesc') },
                { value: 'other', label: t('intake.worldviewOther'), desc: t('intake.worldviewOtherDesc') },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setProfile({ ...profile, worldview: option.value })}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    profile.worldview === option.value
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/15 hover:border-[#D4AF37]/30'
                  }`}
                  data-testid={`worldview-${option.value}`}
                >
                  <div className="font-sans text-[#F8F5F2] font-medium mb-1">
                    {option.label}
                  </div>
                  {option.desc && !option.desc.includes('intake.') && (
                    <div className="font-sans text-sm text-[#A1A1AA]">
                      {option.desc}
                    </div>
                  )}
                </button>
              ))}

              {/* Custom worldview text input when "Other" is selected */}
              <AnimatePresence>
                {profile.worldview === 'other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-3">
                      <label className="font-sans text-sm text-[#A1A1AA] block mb-2">
                        {language === 'tr' ? 'Dünya görüşünüzü kendi kelimelerinizle anlatın:' : 'Describe your worldview in your own words:'}
                      </label>
                      <div className="relative">
                        <textarea
                          value={customWorldview}
                          onChange={(e) => setCustomWorldview(e.target.value)}
                          className="w-full bg-white/5 border border-[#D4AF37]/30 rounded-xl px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none min-h-[100px] resize-none"
                          placeholder={language === 'tr' ? 'Hayatı ve evreni nasıl anlıyorsunuz...' : 'How do you understand life and the universe...'}
                          data-testid="custom-worldview-input"
                        />
                        <div className="absolute right-2 bottom-2">
                          <DictationButton
                            onTranscription={(text) => setCustomWorldview(prev => prev + (prev ? ' ' : '') + text)}
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving || !profile.worldview}
            className="w-full rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg disabled:opacity-50 mt-8"
            data-testid="submit-intake-button"
          >
            {saving ? t('intake.saving') : t('intake.continue')}
          </button>
          
          {/* Skip Option */}
          <button
            type="button"
            onClick={() => navigate('/orbit')}
            className="w-full text-center text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm mt-4 py-2"
            data-testid="skip-intake-button"
          >
            {language === 'tr' ? 'Şimdilik atla, daha sonra tamamla' : 'Skip for now, complete later'}
          </button>
        </motion.form>
      </div>
    </div>
  );
};

export default INTake;
